/**
 * Food service inquiry — component. Each `form[data-food-service-inquiry-form]` gets its own submit listener.
 * Override: window.__foodServiceInquiryApiUrl (default '/apps/public/foodservice-inquiry')
 * Strings: window.foodServiceInquiryStrings
 * Debug: window.__foodServiceInquiryDebug = true logs payload on every submit (errors always log to console).
 *
 * @class FoodServiceInquiryFormComponent
 */
const DEFAULT_API_URL = '/apps/public/foodservice-inquiry';

class FoodServiceInquiryFormComponent {
  /**
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  static str(key, fallback) {
    const S = window.foodServiceInquiryStrings || {};
    return S[key] || fallback;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} selector
   * @returns {string}
   */
  static fieldValue(form, selector) {
    const el = form.querySelector(selector);
    return el ? String(el.value || '').trim() : '';
  }

  /**
   * Country / market label from Doubly (or similar) global. `currentCurrency` may be a string
   * or an object with country-related keys.
   * @returns {string}
   */
  static resolveSubmittedFrom() {
    if (typeof globalThis.getActiveWatkinsCountryForDetectedContent === 'function') {
      try {
        return globalThis.getActiveWatkinsCountryForDetectedContent();
      } catch {
        /* */
      }
    }
    const g = window.DoublyGlobalCurrency;
    if (!g || g.currentCurrency == null || g.currentCurrency === '') {
      return '';
    }
    const c = g.currentCurrency;
    if (typeof c === 'string' || typeof c === 'number') {
      return String(c).trim();
    }
    if (typeof c === 'object') {
      const country =
        c.country ??
        c.countryName ??
        c.country_name ??
        c.currencyCountry ??
        c.name;
      if (country != null && country !== '') {
        return String(country).trim();
      }
    }
    return String(c).trim();
  }

  /**
   * @param {HTMLFormElement} form
   */
  static syncSubmittedFromField(form) {
    const input = form.querySelector('#submitted_from');
    if (input) {
      input.value = FoodServiceInquiryFormComponent.resolveSubmittedFrom();
    }
  }

  /**
   * Shopify customer id as a string (guest / missing → "-1").
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  static customerPayloadValue(form) {
    const raw = FoodServiceInquiryFormComponent.fieldValue(form, '#customer');
    return raw === '' ? '-1' : raw;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string[]}
   */
  static collectStoreTypes(form) {
    const list = [];
    const boxes = form.querySelectorAll('.store-type-list input[type="checkbox"]');
    for (let i = 0; i < boxes.length; i++) {
      if (boxes[i].checked) {
        list.push(boxes[i].value);
      }
    }
    return list;
  }

  /**
   * When the "Other" store type is checked, sends the free-text store name to the API as `storeTypesOther`.
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  static storeTypesOtherValue(form) {
    const otherEl = form.querySelector('#Other');
    if (!otherEl || !otherEl.checked) {
      return '';
    }
    return FoodServiceInquiryFormComponent.fieldValue(form, '#storeOtherDetail');
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {Record<string, unknown>}
   */
  static buildPayload(form) {
    return {
      businessName: FoodServiceInquiryFormComponent.fieldValue(form, '#businessName'),
      firstName: FoodServiceInquiryFormComponent.fieldValue(form, '#firstName'),
      lastName: FoodServiceInquiryFormComponent.fieldValue(form, '#lastName'),
      addressLine1: FoodServiceInquiryFormComponent.fieldValue(form, '#streetAddress'),
      addressLine2: FoodServiceInquiryFormComponent.fieldValue(form, '#addressLine2'),
      city: FoodServiceInquiryFormComponent.fieldValue(form, '#city'),
      province: FoodServiceInquiryFormComponent.fieldValue(form, '#stateProvince'),
      zipCode: FoodServiceInquiryFormComponent.fieldValue(form, '#zipPostalCode'),
      phone: FoodServiceInquiryFormComponent.fieldValue(form, '#phoneNumber'),
      mobile: FoodServiceInquiryFormComponent.fieldValue(form, '#mobileNumber'),
      fax: FoodServiceInquiryFormComponent.fieldValue(form, '#fax'),
      email: FoodServiceInquiryFormComponent.fieldValue(form, '#emailAddress'),
      website: FoodServiceInquiryFormComponent.fieldValue(form, '#website'),
      socialMediaSite: FoodServiceInquiryFormComponent.fieldValue(form, '#socialMediaSites'),
      storeTypes: FoodServiceInquiryFormComponent.collectStoreTypes(form),
      storeTypesOther: FoodServiceInquiryFormComponent.storeTypesOtherValue(form),
      usedWatkinsBefore: FoodServiceInquiryFormComponent.fieldValue(form, '[name="watkins_usage"]'),
      productsInterested: FoodServiceInquiryFormComponent.fieldValue(form, '[name="products_interested"]'),
      estimatedMonthlyVolume: FoodServiceInquiryFormComponent.fieldValue(form, '[name="monthly_volume"]'),
      additionalComments: FoodServiceInquiryFormComponent.fieldValue(form, '[name="additional_comments"]'),
      submitted_from: FoodServiceInquiryFormComponent.fieldValue(form, '#submitted_from'),
      customer: FoodServiceInquiryFormComponent.customerPayloadValue(form),
    };
  }

  /**
   * @param {HTMLFormElement} form
   * @param {'error'|'success'|'idle'} type
   * @param {string} message
   */
  static showFeedback(form, type, message) {
    const errEl = form.querySelector('[data-food-service-inquiry-error]');
    const okEl = form.querySelector('[data-food-service-inquiry-success]');
    if (errEl) {
      errEl.classList.toggle('d-none', type !== 'error');
      errEl.textContent = type === 'error' ? message : '';
    }
    if (okEl) {
      okEl.classList.toggle('d-none', type !== 'success');
      okEl.textContent = type === 'success' ? message : '';
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  static clearFeedback(form) {
    FoodServiceInquiryFormComponent.showFeedback(form, 'idle', '');
    const errEl = form.querySelector('[data-food-service-inquiry-error]');
    const okEl = form.querySelector('[data-food-service-inquiry-success]');
    if (errEl) {
      errEl.classList.add('d-none');
      errEl.textContent = '';
    }
    if (okEl) {
      okEl.classList.add('d-none');
      okEl.textContent = '';
    }
  }

  /**
   * Logs API validation / error details to the console (filter: FoodServiceInquiryFormComponent).
   * Set `window.__foodServiceInquiryDebug = true` to also log every successful submit payload.
   * @param {Record<string, unknown>|null|undefined} data
   */
  static logApiErrorDetails(data) {
    if (!data || typeof data !== 'object') {
      return;
    }
    const keys = [
      'errors',
      'validationErrors',
      'fieldErrors',
      'invalid_fields',
      'fields',
      'details',
      'detail',
    ];
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (data[k] != null && data[k] !== '') {
        console.warn(`[FoodServiceInquiryFormComponent] response.${k}`, data[k]);
      }
    }
  }

  /**
   * @param {SubmitEvent} e
   */
  static onSubmit(e) {
    const form = e.target;
    if (!form || !form.matches || !form.matches('[data-food-service-inquiry-form]')) {
      return;
    }

    e.preventDefault();
    FoodServiceInquiryFormComponent.syncSubmittedFromField(form);
    FoodServiceInquiryFormComponent.clearFeedback(form);

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const storeTypes = FoodServiceInquiryFormComponent.collectStoreTypes(form);
    if (storeTypes.length === 0) {
      FoodServiceInquiryFormComponent.showFeedback(
        form,
        'error',
        FoodServiceInquiryFormComponent.str('storeTypeRequired', 'Please select at least one store type.')
      );
      return;
    }

    const payload = FoodServiceInquiryFormComponent.buildPayload(form);
    const apiUrl = window.__foodServiceInquiryApiUrl || DEFAULT_API_URL;

    const submitBtn = form.querySelector('button[type="submit"]');
    Utility.setSubmitButtonLoading(submitBtn, true);

    if (window.__foodServiceInquiryDebug) {
      console.log('[FoodServiceInquiryFormComponent] submit', { apiUrl, payload });
    }

    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    })
      .then((r) =>
        r
          .json()
          .then((data) => ({ ok: r.ok, status: r.status, data }))
          .catch(() => ({ ok: r.ok, status: r.status, data: {} }))
      )
      .then((result) => {
        if (result.ok) {
          if (window.__foodServiceInquiryDebug) {
            console.log('[FoodServiceInquiryFormComponent] success', { status: result.status, data: result.data });
          }
          FoodServiceInquiryFormComponent.showFeedback(
            form,
            'success',
            FoodServiceInquiryFormComponent.str('submitSuccess', 'Your inquiry was submitted. A representative will contact you within 2-3 business days.')
          );
          form.reset();
        } else {
          const data = result.data;
          console.warn('[FoodServiceInquiryFormComponent] API error', {
            httpStatus: result.status,
            responseBody: data,
            submittedPayload: payload,
          });
          FoodServiceInquiryFormComponent.logApiErrorDetails(data);
          const msg =
            (data && (data.message || data.error || data.detail)) ||
            `${FoodServiceInquiryFormComponent.str('submitError', 'Submission could not be completed. Please try again.')} (${result.status})`;
          FoodServiceInquiryFormComponent.showFeedback(form, 'error', msg);
        }
      })
      .catch((err) => {
        console.warn('[FoodServiceInquiryFormComponent] network / fetch failure', err);
        FoodServiceInquiryFormComponent.showFeedback(
          form,
          'error',
          FoodServiceInquiryFormComponent.str('networkError', 'Network error. Please try again later.')
        );
      })
      .finally(() => {
        Utility.setSubmitButtonLoading(submitBtn, false);
      });
  }

  /**
   * Registers document-level submit handler for food service inquiry forms.
   */
  static bind() {
    document.querySelectorAll('form[data-food-service-inquiry-form]').forEach((form) => {
      if (form.__watkinsFoodServiceInquiryBound) {
        return;
      }
      form.__watkinsFoodServiceInquiryBound = true;
      form.addEventListener('submit', FoodServiceInquiryFormComponent.onSubmit);
    });
  }

  static syncAllSubmittedFromFields() {
    document.querySelectorAll('[data-food-service-inquiry-form]').forEach((form) => {
      FoodServiceInquiryFormComponent.syncSubmittedFromField(form);
    });
  }

  /**
   * Initializes food service inquiry (idempotent).
   */
  static init() {
    if (window.__watkinsFoodServiceInquiryLoaded) {
      return;
    }
    window.__watkinsFoodServiceInquiryLoaded = true;

    const start = () => {
      FoodServiceInquiryFormComponent.bind();
      FoodServiceInquiryFormComponent.syncAllSubmittedFromFields();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }

    window.addEventListener('load', () => FoodServiceInquiryFormComponent.syncAllSubmittedFromFields());
  }
}

FoodServiceInquiryFormComponent.init();
